/* eslint-disable no-underscore-dangle */
/* eslint-disable @stencil-community/required-jsdoc */
import { Memoize, isEmpty, parseIfString } from '@springbokagency/stencil-base';
import { Component, Host, Listen, Prop, State, getAssetPath, h } from '@stencil/core';

import { deepQuerySelector } from '../../../utils/deepQuerySelector';

import type { ModalVideoGroupItem } from '../../organisms/video-modal/video-modal.types';
import type { LayoutData } from './default-layout.types';
import state from '../../../store/store';
import pageTracking from '../../../utils/pageTracking';

@Component({
  tag: 'efb-default-layout',
  styleUrl: 'default-layout.scss',
  shadow: true,
})
export class EfbDefaultLayout {
  /**
   * State
   */

  @State() isVideoModalOpen = false;

  @State() activeVideoGroupId: string;

  @State() activeVideoId: string;

  @State() previousWidth: number;

  /**
   * Props
   */

  @Prop() data: LayoutData | string;

  @Memoize('data')
  get parsedData(): LayoutData {
    return parseIfString(this.data);
  }

  @Prop() videos?: ModalVideoGroupItem[] | string;

  @Memoize('videos')
  get parsedVideos(): ModalVideoGroupItem[] {
    return parseIfString(this.videos);
  }

  @Prop() showFooterVisual?: boolean = true;

  /**
   * Events
   */

  @Listen('openVideoModal')
  openVideoModalHandler(e: CustomEvent) {
    this.openVideoModal(e.detail.videoModalGroup);
  }

  @Listen('closeModal')
  closeModalHandler(e: CustomEvent) {
    const { modalId } = e.detail;

    if (modalId === 'video-modal') {
      this.isVideoModalOpen = false;
      this.activeVideoGroupId = null;
      this.activeVideoId = null;
    }
  }

  @Listen('efbClickAction')
  onEfbClickActionHandler(e: CustomEvent) {
    const { name, value, dataAnalytics } = e.detail;

    // trigger manually button tracking
    if (!isEmpty(dataAnalytics) && !isEmpty(window._satellite)) {
      window._satellite.track('trackClick', { dataAnalytics });
    }

    switch (name) {
      case 'open-video-modal':
        this.openVideoModal(value.videoGroupId, value.videoId);

        break;
      default:
        break;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  @Listen('efbButtonClick')
  onEfbButtonClickHandler(e: CustomEvent) {
    const { dataAnalytics } = e.detail;

    // trigger manually button tracking
    if (!isEmpty(dataAnalytics) && !isEmpty(window._satellite)) {
      window._satellite.track('trackClick', { dataAnalytics });
    }
  }

  /**
   * Methods
   */

  private openVideoModal(videoGroup: string, videoId: string = '') {
    this.activeVideoGroupId = videoGroup;
    this.activeVideoId = videoId;
    this.isVideoModalOpen = true;
  }

  private handleUrlHash(hash: string) {
    const hashString = hash.replace(/^#/, '');
    const hashParams = new URLSearchParams(hashString);

    // open video modal if has necassary url param
    if (!isEmpty(hashParams.get('vgroup'))) {
      this.handleVideoModalURLParams(hashParams);
    } else {
      this.scrollToSection(hash);
    }
  }

  private handleVideoModalURLParams(params: URLSearchParams) {
    const videoGroup = params.get('vgroup');
    const videoId = params.get('vid');

    const activeVideoGroup = this.parsedVideos.find(
      (group: ModalVideoGroupItem) => group.modalGroupId === videoGroup
    );

    // do not open video modal if video-group doesn't exist
    if (isEmpty(activeVideoGroup)) return;

    this.activeVideoGroupId = videoGroup;
    this.activeVideoId = videoId;

    this.isVideoModalOpen = true;
  }

  // eslint-disable-next-line class-methods-use-this
  private scrollToSection(hash: string) {
    const element = deepQuerySelector(hash, document);

    if (!isEmpty(element)) {
      const yOffset = -35;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

      setTimeout(() => {
        window.scrollTo({ top: y, behavior: 'smooth' });
      }, 700);
    }
  }

  private doesWidthChange() {
    const currWidth = window.innerWidth;
    const isLarger = currWidth !== this.previousWidth;

    // change current width
    this.previousWidth = currWidth;

    return isLarger;
  }

  private defineViewportHeight() {
    // first we get the viewport height and we multiple it by 1% to get a value for a vh unit
    const vh = window.innerHeight * 0.01;

    // then we set the value in the --vh custom property to the root of the document
    // not when width doesn't change to avoid slight layout shift on mobile when scrolling and address bar is hidding
    if (this.doesWidthChange()) {
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private setMetaTags() {
    /**
     * change og:image
     */
    const ogImageTag = document.createElement('meta');

    ogImageTag.setAttribute('property', 'og:image');

    ogImageTag.setAttribute(
      'content',
      getAssetPath(`/dist/assets/images/sharing/OG-image-Failbetter-${state.currentLanguage}.png`)
    );

    // Find the existing og:image (if any) and remove it
    const existingOGImage = deepQuerySelector('meta[property="og:image"]', document);

    if (!isEmpty(existingOGImage)) {
      existingOGImage.remove();
    }

    document.head.appendChild(ogImageTag);

    /**
     * change favicon
     */
    const faviconLink = document.createElement('link');

    faviconLink.setAttribute('rel', 'icon');
    faviconLink.setAttribute('type', 'image/png');
    faviconLink.setAttribute('href', getAssetPath('/dist/assets/images/logos/favicon.png'));

    // Find the existing favicon (if any) and remove it
    const existingFavicon = deepQuerySelector('link[rel="icon"]', document);

    if (!isEmpty(existingFavicon)) {
      existingFavicon.remove();
    }

    // Append the new <link> tag to the <head> section of the document
    document.head.appendChild(faviconLink);
  }

  /**
   * Lifecycle Hooks
   */

  componentWillLoad() {
    // define and store page's language
    state.currentLanguage = this.parsedData.language;
  }

  componentDidLoad() {
    this.defineViewportHeight();

    // handle url's hash
    const urlHash = window.location.hash;

    if (!isEmpty(urlHash)) this.handleUrlHash(urlHash);

    // scroll to id when click on hash link
    window.addEventListener('hashchange', () => {
      this.handleUrlHash(window.location.hash);
    });

    // on resize event
    window.addEventListener('resize', this.defineViewportHeight.bind(this));

    // trigger manually page tracking
    pageTracking(state.currentLanguage, this.parsedData?.pageAnalyticsId);

    // set <head> meta tags
    this.setMetaTags();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.defineViewportHeight.bind(this));
  }

  /**
   * Render
   */

  render() {
    return (
      <Host>
        <div class="default-layout">
          {/* Header */}
          {!isEmpty(this.parsedData?.header) && <efb-header data={this.parsedData.header} />}

          {/* Page content */}
          <slot />

          {/* Footer */}

          {!isEmpty(this.parsedData?.footer) && (
            <efb-footer
              data={this.parsedData.footer}
              socialLinks={this.parsedData.socialLinks}
              has-background-visual={this.showFooterVisual}
            />
          )}

          {/* Video modal */}
          {this.isVideoModalOpen && (
            <efb-video-modal
              modalData={this.parsedData.videoModal}
              videoGroups={this.parsedVideos}
              activeVideoGroupId={this.activeVideoGroupId}
              activeVideoId={this.activeVideoId}
            />
          )}
        </div>
      </Host>
    );
  }
}
